<template>
  <div
    v-click-outside="clickOutSideThemesSwitcher"
    class="wrapper"
  >
    <div
      class="theme-switcher"
      :class="{'theme-switcher--active': isShowThemesSwitcher}"
      :style="`width: ${newsPageRoofWidth >= 1400 ? 219 : 122}px;`"
      @click="isShowThemesSwitcher = !isShowThemesSwitcher"
    >
      <span
        class="theme-switcher__title"
        :class="{'theme-switcher__box-title--active': isShowThemesSwitcher}"
      >
        {{ t('news_page_themes_switcher.label.theme') }}
        <template v-if="typeof themesDisplayText === 'number'">
          ({{ themesDisplayText }})
        </template>
      </span>
      <span
        v-if="typeof themesDisplayText === 'string'"
        class="theme-switcher__name"
      >
        {{ themesDisplayText }}
      </span>
      <ArrowBottomIcon
        class="theme-switcher__icon"
        :class="{'theme-switcher__icon--active': isShowThemesSwitcher}"
      />
    </div>

    <div
      v-if="isShowThemesSwitcher"
      class="theme-switcher__list"
    >
      <NewsPageThemesList
        :active-theme="activeTheme"
        :is-all-themes-chosen="isAllThemesChosen"
        @set-active-theme="theme => setActiveTheme(theme)"
        @open-delete-popup="(val) => openDeletePopup(val)"
        @open-edit-popup="openEditPopup"
      />
      <div
        v-if="blockStore.allThemes.length === 0 && !rubricsStore.rubricLoader"
        class="theme-switcher__empty"
      >
        {{ t('news_page_themes_switcher.label.not_found') }}
      </div>
      <ButtonAccentPrimary
        v-if="LKStore.user.roleNames.includes('аналитик') || LKStore.project.isOwner"
        class="theme-switcher__button"
        @click="setActiveTheme(null); openEditPopup()"
      >
        {{ t('news_page_themes_switcher.button.new') }}
      </ButtonAccentPrimary>
    </div>


    <NewsPageThemesEditPopup
      v-if="isShowThemesEditPopup"
      :theme="activeTheme"
      :is-new-theme="isNewTheme"
      @open-delete-popup="openDeletePopup"
      @close-edit-popup="closeEditPopup"
    />

    <Modal
      v-if="isShowDeletePopup"
      teleport="#app"
      :title="t('news_page_popup_delete.title.delete_theme')"
      :confirm-text="t('news_page_popup_delete.button.accept_delete')"
      :cancel-text="t('news_page_popup_delete.button.cancel_button')"
      :is-confirm-pressed="isPressedDeletePopupButton"
      @confirm="deleteTheme(activeTheme.id)"
      @cancel="handleDeleteModalClose"
    >
      {{ t('news_page_popup_delete.message.delete_theme') }}
    </Modal>
  </div>
</template>

<script setup>
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { defineProps, ref, computed } from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useLK } from "@/controllerLK/LKstore";
import { t } from "@/controllerLK/GlobusLocalization";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import NewsPageThemesList from "@/components/NewsPage/NewsPageRoof/NewsPageThemesList.vue";
import NewsPageThemesEditPopup from "@/components/NewsPage/NewsPagePopup/NewsPageThemesEditPopup.vue";
import { useBlockStore } from "@/store/NewsFeed/blocks";

const props = defineProps({
  newsPageRoofWidth: {
    type: Number,
    required: true
  }
});

// store
const LKStore = useLK();
const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();
const blockStore = useBlockStore();

// Флаг. Выбраны ли все темы.
const isAllThemesChosen = computed(
  () => blockStore.allThemes.length > 0 ? blockStore.allThemes.length === blockStore.chosenThemes.length : false);

// Отображение модального окна редактирования тем
const isShowThemesEditPopup = ref(false);

// Отображение выпадающего списка выбора тем
const isShowThemesSwitcher = ref(false);

// Отображение модального окна удаления темы.
const isShowDeletePopup = ref(false);
// Флаг отправки темы на удаление.
const isPressedDeletePopupButton = ref(false);

// Флаг создания новой темы или редактирования старой
const isNewTheme = ref(false);

// Активная тема для редактирования/создания
const activeTheme = ref(null);

// Обработка события клика вне переключателя тем
const clickOutSideThemesSwitcher = () => {
  if (!isShowDeletePopup.value && !isShowThemesEditPopup.value) {
    isShowThemesSwitcher.value = false;
  }
};

// Обработка события закрытия окна удаления тем
const handleDeleteModalClose = () => {
  isShowDeletePopup.value = false;
};

// Установка активной темы для редактирования/создания
const setActiveTheme = (theme) => {
  if (theme) {
    isNewTheme.value = false;
    activeTheme.value = theme;
  } else {
    isNewTheme.value = true;
    activeTheme.value = newTheme;
  }

};

// Событие открытия модального окна удаления темы
const openDeletePopup = () => {
  isShowDeletePopup.value = true;
};


// Функция удаления темы.
async function deleteTheme(item) {
  isPressedDeletePopupButton.value = true;
  await blockStore.deleteTheme(item);
  isShowThemesEditPopup.value = false;
  isShowDeletePopup.value = false;

  const chosenThemeIndex = blockStore.chosenThemes.indexOf(item);

  // Если удаляемая тема, есть в выбранных. Убираем ее из выбранных.
  if (chosenThemeIndex > -1) {
    blockStore.chosenThemes.splice(chosenThemeIndex, 1);
  }

  // Если удаляют единственную активную тему. Сброс выбранных тем на все.
  if (blockStore.chosenThemes.length === 1 && blockStore.chosenThemes[0] === item) {
    rubricsStore.chosenRubrics = rubricsStore.rubricsOnMonitoring;
    blockStore.chosenThemes = [];
    newsFeedStore.clearNewsFeed();
    await newsFeedStore.getNews();
  }
  isPressedDeletePopupButton.value = false;
}

const newTheme = {
  name: "",
  rubric_ids: [],
};

// Событие открытие модального окна редактирования темы
const openEditPopup = () => {
  isShowThemesEditPopup.value = true;
};

// Событие закрытия модального окна редактирования темы
const closeEditPopup = () => {
  activeTheme.value = null;
  isShowThemesEditPopup.value = false;
};

// Форматирование текста в поле выбора темы в зависимости от количества выбранных тем.
const themesDisplayText = computed(() => {
  if (props.newsPageRoofWidth >= 1400) {
    if (isAllThemesChosen.value) {
      return t("news_page_themes_switcher.label.theme_all");
    }

    if (blockStore.chosenThemes.length === 0) {
      return t("news_page_themes_switcher.label.theme_not_selected");
    }

    if (blockStore.chosenThemes.length === 1) {
      return blockStore.allThemes.find((theme) => theme.id === blockStore.chosenThemes[0])?.name;
    }

    if (blockStore.chosenThemes.length > 1) {
      return blockStore.chosenThemes.length;
    }

    return "";
  } else {
    if (isAllThemesChosen.value) {
      return t("news_page_themes_switcher.label.theme_all");
    } else {
      return blockStore.chosenThemes.length;
    }
  }
});

</script>

<style lang="scss">
.wrapper {
  position: relative;
}

.theme-switcher {
  background: var(--background-bg-white);
  display: flex;
  border-radius: 8px;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;
  height: 100%;
  align-items: center;
  gap: 4px;

  &--active {
    background: var(--background-bg-hover2);
  }

  &:hover {
    background: var(--background-bg-hover2);
  }

  &__button {
    width: 100%;
  }

  &__empty {
    width: 100%;
    height: 140px;
    background: var(--background-01);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    padding: 30px 20px;

    p {
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }

  &__loader {
    width: 200px;
    height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__list {
    padding: 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    position: absolute;
    top: 110%;
    left: 0;
    min-width: 240px;
    max-width: 350px;
    background: var(--background-bg-white);
    z-index: 5;
    box-shadow: 1px 2px 10px 0 #131e2726;
    gap: 10px;
  }

  &__icon {
    min-width: 17px;
    min-height: 17px;
    margin-left: auto;
    color: var(--icon-icon-secondary);

    &--active {
      rotate: 180deg;
      color: var(--icon-icon-on);
    }
  }

  &__title {
    color: var(--text-text-main);
    line-height: 18px;
    font-weight: 400;
    font-size: 14px;
    white-space: nowrap;

    &--active {
      color: var(--text-text-on);
    }
  }

  &__name {
    white-space: nowrap;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text-text-pur);
  }
}
</style>